import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { subMonths, parseISO, isBefore } from 'date-fns';
import { View } from 'react-native';
import Animated, { Easing, withDelay, withTiming, useAnimatedStyle, useAnimatedScrollHandler, } from 'react-native-reanimated';
import { rem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS, } from 'design-system/values';
import { isDefaultCategory, useColorOfCategory } from 'hooks/useCategory';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { setAnalyticsPosition } from 'actions/expenses';
import { useMarginBottom } from 'hooks/useMarginBottom';
import createStyleSheets from 'utils/createStyleSheets';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import { fetchCommittedForCurrentPaydayRange } from 'features/subscriptions/actions';
import { CARD_VIEW_BORDER_RADIUS } from 'design-system';
import useWalkthroughLayout from 'features/feed/components/walkthrough/useWalkthroughLayout';
import Walkthrough from 'features/feed/components/walkthrough/Walkthrough';
import { useShowWalkthroughV6 } from 'features/feed/components/walkthrough/hooks/useShowWalkthroughV6';
import { useOpenCategory } from '../hooks/useOpenCategory';
import useSelectedCategory from '../hooks/useSelectedCategory';
import { selectAnalyticsCategoryCurrentLoadingState, selectAnalyticsCategorySections, selectAnalyticsMerchantCurrentLoadingState, selectAnalyticsMerchantSections, selectAnalyticsPosition, selectChartType, selectMonthlyTotals, selectMonthlyTotalsFilteredWithFallbackMonths, } from '../selectors';
import { ChartType } from '../types';
import AnalyticsSectionHeader from './list/AnalyticsSectionHeader';
import AnalyticsSectionListEmpty from './list/AnalyticsSectionListEmpty';
import AnalyticsSectionFooter from './list/AnalyticsSectionListFooter';
import AnalyticsSectionListHeader from './list/AnalyticsSectionListHeader';
import RowCategory from './row/RowCategory';
import RowMerchant from './row/RowMerchant';
import { isWeb } from '../../../constants';
function withAnimation(toValue) {
    'worklet';
    return withTiming(toValue, {
        duration: 300,
        easing: Easing.inOut(Easing.cubic),
    });
}
const keyExtractor = (item) => {
    switch (item.type) {
        case 'marker':
            return 'marker';
        case 'header':
            return item.data.title;
        default:
            return item.data.id.toString();
    }
};
const MARKER_HEIGHT = rem(49);
const ITEM_HEIGHT = rem(77);
const HEADER_HEIGHT = rem(64);
const AnalyticsList = ({ scrollY }) => {
    const unfilteredMonthlyTotals = useAppSelector(selectMonthlyTotals);
    const filteredMonthlyTotals = useAppSelector(selectMonthlyTotalsFilteredWithFallbackMonths);
    const monthlyTotals = filteredMonthlyTotals?.results || unfilteredMonthlyTotals;
    const showWalkthroughV6 = useShowWalkthroughV6();
    const { itemPositions, scrollHeight, scrollLayoutHeight, onContentSizeChange, onLayout: onLayoutForWalkthrough, onScrollLayout, } = useWalkthroughLayout();
    const onLayoutChartSettings = useCallback((event) => {
        onLayoutForWalkthrough('analyticsChartSettings', event);
    }, [onLayoutForWalkthrough]);
    const onAnalyticsChartLayout = useCallback((event) => {
        onLayoutForWalkthrough('analyticsChart', event);
    }, [onLayoutForWalkthrough]);
    const position = useAppSelector(selectAnalyticsPosition);
    const dispatch = useAppDispatch();
    useEffect(() => {
        dispatch(fetchCommittedForCurrentPaydayRange());
    }, []);
    const openCategory = useOpenCategory(monthlyTotals, position, 'Category', filteredMonthlyTotals?.step, filteredMonthlyTotals?.accountIds);
    useEffect(() => () => dispatch(setAnalyticsPosition(0, 0, true)), []);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        // eslint-disable-next-line no-param-reassign
        scrollY.value = handle.contentOffset.y;
    }, []);
    const chartTypeToShow = useAppSelector(selectChartType);
    const isPieChart = chartTypeToShow === ChartType.categories;
    const [selected, setSelection] = useState(0);
    const colorOfCategory = useColorOfCategory();
    const styles = useStyles(styleSet);
    const { selectedCategoryId } = useSelectedCategory();
    const categorySections = useAppSelector(selectAnalyticsCategorySections);
    const merchantSections = useAppSelector(selectAnalyticsMerchantSections);
    const categoriesLoading = useAppSelector(selectAnalyticsCategoryCurrentLoadingState);
    const merchantsLoading = useAppSelector(selectAnalyticsMerchantCurrentLoadingState);
    const colors = useColors();
    const categoryKeyIndexMap = useMemo(() => {
        const expenses = categorySections.filter((s) => s.type === 'category');
        return expenses.reduce((result, exp, index) => {
            if (exp.type === 'category') {
                const backgroundColor = exp.data
                    ? colorOfCategory(exp.data)
                    : undefined;
                return {
                    ...result,
                    [exp.data.id]: {
                        index,
                        backgroundColor,
                    },
                };
            }
            return result;
        }, {});
    }, [colorOfCategory, categorySections]);
    const [headerHeight, setHeaderHeight] = useState(0);
    const markerAnimatedStyle = useAnimatedStyle(() => {
        if (selected !== 0)
            return {};
        const categoryRowData = selectedCategoryId.value
            ? categoryKeyIndexMap[selectedCategoryId.value]
            : undefined;
        return {
            transform: [
                {
                    translateY: withAnimation((categoryRowData?.index || 0) * ITEM_HEIGHT +
                        ITEM_HEIGHT / 2 -
                        MARKER_HEIGHT / 2),
                },
            ],
            backgroundColor: withAnimation(categoryRowData?.backgroundColor || colors.categories.custom),
            opacity: categoryRowData && isPieChart ? withDelay(100, withTiming(1)) : 0,
        };
    }, [
        selected,
        selectedCategoryId,
        categoryKeyIndexMap,
        colors.categories.custom,
        isPieChart,
    ]);
    const onLayout = useCallback((event) => {
        if (event.nativeEvent.layout.height > headerHeight && isPieChart) {
            setHeaderHeight(event.nativeEvent.layout.height);
        }
    }, [isPieChart, headerHeight]);
    const { isUnlockedForSpace, onPressUpgrade } = useShouldShowUpgrade({
        benefitId: 'customCategories',
    });
    const { isUnlockedForSpace: isPreviousPeriodsUnlockedForSpace, onPressUpgrade: onPressUpgradeForSearch, } = useShouldShowUpgrade({
        benefitId: 'search',
    });
    const twoMonthsAgo = useMemo(() => subMonths(new Date(), 2), []);
    const positionToBlurFrom = useMemo(() => {
        if (isPreviousPeriodsUnlockedForSpace) {
            return undefined;
        }
        const index = monthlyTotals.findIndex((item) => isBefore(parseISO(item.from), twoMonthsAgo));
        if (index > -1) {
            return index;
        }
        return undefined;
    }, [isPreviousPeriodsUnlockedForSpace, monthlyTotals, twoMonthsAgo]);
    const { paddingHorizontalStyle, paddingHorizontal } = useAppFrameDimensions(isWeb);
    const renderItem = useCallback(({ item }) => {
        const blurValue = positionToBlurFrom
            ? position >= positionToBlurFrom
            : false;
        switch (item.type) {
            case 'marker':
                return (<Animated.View style={[
                        styles.marker,
                        { marginLeft: (paddingHorizontal || 0) + rem(16) },
                        markerAnimatedStyle,
                    ]}/>);
            case 'merchant':
                return (<View style={[
                        styles.rowContainer,
                        item.index === 0 && styles.rowFirstItem,
                        item.index === 0 && styles.rowFirstItemMerchant,
                        item.isLast && styles.rowLastItem,
                    ]}>
              <RowMerchant merchant={item.data} onPress={openCategory} blurValue={blurValue}/>
            </View>);
            case 'category':
                return (<View style={[
                        styles.rowContainer,
                        item.index === 0 && styles.rowFirstItem,
                        item.isLast && styles.rowLastItem,
                    ]}>
              <RowCategory category={item.data} totalSpending={item.totalSpending} showExpensePercentage={!item.data.hideFromStats} onPress={openCategory} onPressUpgrade={!isUnlockedForSpace && !isDefaultCategory(item.data.id)
                        ? onPressUpgrade
                        : undefined} selectable={Boolean(isPieChart && item.data.total < 0)} selectedCategoryId={selectedCategoryId} blurValue={blurValue}/>
            </View>);
            case 'header':
                return (<AnalyticsSectionHeader id={item.data.id} title={item.data.title} amount={item.data.amount} currency={item.data.currency}/>);
            default:
                return null;
        }
    }, [
        positionToBlurFrom,
        position,
        styles.marker,
        styles.rowContainer,
        styles.rowFirstItem,
        styles.rowFirstItemMerchant,
        styles.rowLastItem,
        paddingHorizontal,
        markerAnimatedStyle,
        openCategory,
        isPieChart,
        isUnlockedForSpace,
        onPressUpgrade,
        selectedCategoryId,
    ]);
    const renderItemWrapper = useCallback((props) => <View style={paddingHorizontalStyle}>{renderItem(props)}</View>, [paddingHorizontalStyle, renderItem]);
    const renderListFooter = useMemo(() => (<View style={paddingHorizontalStyle}>
        <AnalyticsSectionFooter />
      </View>), [paddingHorizontalStyle]);
    const renderListHeader = useMemo(() => (<AnalyticsSectionListHeader filteredMonthlyTotals={filteredMonthlyTotals} monthlyTotals={monthlyTotals} position={position} positionToBlurFrom={positionToBlurFrom} setSelection={setSelection} onLayout={onLayout} onAnalyticsChartLayout={onAnalyticsChartLayout} onLayoutChartSettings={onLayoutChartSettings} contentContainerStyle={paddingHorizontalStyle} onPressUpgradeForSearch={onPressUpgradeForSearch}/>), [
        filteredMonthlyTotals,
        monthlyTotals,
        onAnalyticsChartLayout,
        onLayout,
        onLayoutChartSettings,
        onPressUpgradeForSearch,
        paddingHorizontalStyle,
        position,
        positionToBlurFrom,
    ]);
    const emptyComponent = useMemo(() => (<View style={paddingHorizontalStyle}>
        <AnalyticsSectionListEmpty isFetching={selected === 0 ? categoriesLoading : merchantsLoading}/>
      </View>), [categoriesLoading, merchantsLoading, paddingHorizontalStyle, selected]);
    const paddingBottom = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING);
    // Ensures the marker is on top of the list
    const CellRendererComponentStyle = useCallback(({ item }) => ({
        zIndex: item.type === 'marker' ? 1 : 0,
    }), []);
    return (<>
      <Animated.FlatList onScroll={scrollHandler} scrollEventThrottle={16} onLayout={onScrollLayout} keyExtractor={keyExtractor} renderItem={renderItemWrapper} ListEmptyComponent={emptyComponent} contentContainerStyle={paddingBottom} ListHeaderComponent={renderListHeader} ListFooterComponent={renderListFooter} onContentSizeChange={onContentSizeChange} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} data={selected === 0 ? categorySections : merchantSections} CellRendererComponentStyle={CellRendererComponentStyle}/>
      {!!showWalkthroughV6 && (<Walkthrough scrollHeight={scrollHeight} itemPositions={itemPositions} scrollLayoutHeight={scrollLayoutHeight}/>)}
    </>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        flex: 1,
    },
    rowContainer: {
        marginHorizontal: rem(16),
        backgroundColor: colors.cards.onDark,
        ...(isWeb && {
            paddingHorizontal: rem(8),
        }),
    },
    rowFirstItemMerchant: {
        marginTop: rem(16),
    },
    rowFirstItem: {
        borderTopLeftRadius: CARD_VIEW_BORDER_RADIUS,
        borderTopRightRadius: CARD_VIEW_BORDER_RADIUS,
        ...(isWeb && {
            paddingTop: rem(8),
        }),
    },
    rowLastItem: {
        borderBottomLeftRadius: CARD_VIEW_BORDER_RADIUS,
        borderBottomRightRadius: CARD_VIEW_BORDER_RADIUS,
        ...(isWeb && {
            paddingBottom: rem(8),
        }),
    },
    marker: {
        height: MARKER_HEIGHT,
        width: rem(4),
        marginLeft: rem(16),
        position: 'absolute',
        left: rem(4),
        top: HEADER_HEIGHT,
        borderRadius: rem(5),
    },
}));
export default memo(AnalyticsList);
